import { useState } from "react";
import { useNavigate, useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Heart, Minus, Plus, ShoppingCart, ThumbsUp } from "lucide-react";
import {
  getProductById,
  getReviewsForProduct,
  getCategoryMeta,
} from "../data/products";
import { useStore } from "../context/StoreContext";
import Coin from "../components/Coin";
import { toast } from "sonner";
import sfx from "../utils/sound";
import usePageMeta from "../hooks/usePageMeta";

const Product = () => {
  const { id } = useParams();
  const product = getProductById(id);
  const { addToCart, toggleWishlist, wishlist, coins } = useStore();
  const [qty, setQty] = useState(1);
  const nav = useNavigate();

  usePageMeta(
    product ? `${product.name} · 67Cafe` : "Product · 67Cafe",
    product
      ? `${product.name} — ${product.desc} Only ${product.price} pretend coins in the 67Cafe kids' store.`
      : "A pretend product on 67Cafe"
  );

  if (!product) return <Navigate to="/shop" replace />;
  const cat = getCategoryMeta(product.category);
  const reviews = getReviewsForProduct(product);
  const isFav = wishlist.includes(product.id);
  const total = product.price * qty;
  const canAfford = coins >= total;

  const handleAdd = () => {
    addToCart(product, qty);
    sfx.add();
    toast(`Added ${qty} × ${product.name} to your cart!`, { duration: 1400 });
  };

  return (
    <div
      className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-8"
      data-testid={`product-page-${product.id}`}
    >
      <Link
        to={`/category/${product.category}`}
        className="btn-3d btn-sm btn-ghost mb-4"
        data-testid="product-back-btn"
      >
        <ArrowLeft size={18} strokeWidth={3} /> Back to {cat.name}
      </Link>

      <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
        <div
          className="chunky-card p-6 flex items-center justify-center relative"
          style={{ background: product.color + "26", minHeight: 320 }}
        >
          <button
            onClick={() => {
              toggleWishlist(product.id);
              sfx.fav();
              toast(isFav ? "Removed from wishlist" : "Added to wishlist 💖", {
                duration: 1400,
              });
            }}
            className="absolute top-4 right-4 rounded-full flex items-center justify-center"
            aria-label="Favorite"
            data-testid="product-fav-btn"
            style={{
              width: 52,
              height: 52,
              background: isFav ? "#F368E0" : "#fff",
              border: "3px solid #2b2b2b",
              boxShadow: "0 4px 0 #2b2b2b",
            }}
          >
            <Heart
              size={26}
              strokeWidth={3}
              color={isFav ? "white" : "#F368E0"}
              fill={isFav ? "white" : "none"}
            />
          </button>
          <span
            className="bob"
            style={{
              fontSize: 200,
              lineHeight: 1,
              filter: "drop-shadow(0 6px 0 rgba(0,0,0,0.18))",
            }}
            aria-hidden="true"
          >
            {product.emoji}
          </span>
        </div>

        <div className="flex flex-col gap-4">
          <div
            className="inline-flex items-center gap-2 self-start rounded-full px-3 py-1"
            style={{
              background: cat.color,
              border: "3px solid #2b2b2b",
              color: "#fff",
              boxShadow: "0 3px 0 #2b2b2b",
            }}
          >
            <span>{cat.emoji}</span>
            <span className="font-display text-sm">{cat.name}</span>
          </div>
          <h1
            className="font-display text-3xl sm:text-4xl leading-tight"
            data-testid="product-detail-name"
          >
            {product.name}
          </h1>
          <p className="text-lg" style={{ color: "#484848" }}>
            {product.desc}
          </p>

          <div className="flex items-center gap-3">
            <span
              className="coin-pill"
              style={{ fontSize: 22 }}
              data-testid="product-detail-price"
            >
              <Coin size={26} />
              {product.price}
            </span>
            <span className="text-sm" style={{ color: "#484848" }}>
              per item
            </span>
          </div>

          <div className="flex items-center gap-3 mt-2">
            <div
              className="inline-flex items-center rounded-full overflow-hidden"
              style={{ border: "3px solid #2b2b2b", boxShadow: "0 4px 0 #2b2b2b" }}
            >
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="p-3"
                aria-label="Decrease quantity"
                data-testid="qty-decrease"
                style={{ background: "#FFF9E6" }}
              >
                <Minus size={20} strokeWidth={3} />
              </button>
              <span
                className="px-5 font-display text-xl min-w-[52px] text-center"
                data-testid="qty-value"
                style={{ background: "#fff" }}
              >
                {qty}
              </span>
              <button
                onClick={() => setQty((q) => Math.min(20, q + 1))}
                className="p-3"
                aria-label="Increase quantity"
                data-testid="qty-increase"
                style={{ background: "#FFF9E6" }}
              >
                <Plus size={20} strokeWidth={3} />
              </button>
            </div>
            <span
              className="text-base"
              style={{ color: canAfford ? "#484848" : "#FF3B30" }}
              data-testid="product-total"
            >
              Total: <strong>{total} coins</strong>
              {!canAfford && " · not enough coins"}
            </span>
          </div>

          <div className="flex flex-wrap gap-3 mt-2">
            <button
              className="btn-3d flex-1 min-w-[180px]"
              onClick={handleAdd}
              data-testid="product-add-cart-btn"
            >
              <Plus size={22} strokeWidth={3} />
              Add to Cart
            </button>
            <button
              className="btn-3d btn-accent flex-1 min-w-[180px]"
              disabled={!canAfford}
              onClick={() => {
                addToCart(product, qty);
                sfx.add();
                nav("/cart");
              }}
              data-testid="product-buy-now-btn"
            >
              <ShoppingCart size={22} strokeWidth={3} />
              Buy Now
            </button>
          </div>
        </div>
      </div>

      {/* Character reviews */}
      <section className="mt-10">
        <h2 className="font-display text-2xl mb-4">
          <span className="wiggle inline-block mr-2">🎤</span>
          What our friends say
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {reviews.map((r, i) => (
            <div
              key={i}
              className="chunky-card p-4 flex items-start gap-4"
              data-testid={`review-${i}`}
              style={{ background: "#fff" }}
            >
              <div
                className="rounded-full flex items-center justify-center shrink-0 text-4xl"
                style={{
                  width: 64,
                  height: 64,
                  background: "#FFF9E6",
                  border: "3px solid #2b2b2b",
                }}
                aria-hidden="true"
              >
                {r.avatar}
              </div>
              <div>
                <div className="font-display text-lg">{r.name}</div>
                <div className="flex items-center gap-1 my-1" style={{ color: "#FFB400" }}>
                  {"★★★★★".split("").map((s, j) => (
                    <span key={j}>{s}</span>
                  ))}
                </div>
                <p style={{ color: "#484848" }}>"{r.text}"</p>
                <div
                  className="text-sm mt-2 inline-flex items-center gap-1"
                  style={{ color: "#00A699" }}
                >
                  <ThumbsUp size={14} strokeWidth={3} /> Verified imagination
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Product;
